import type { FormValues, JsonValue } from '@funkit/connect-core'

import { parseCalendarDate } from '../calendarDate'
import type { StripeKycAddress, StripeKycInfo } from './stripeOnramp'

/**
 * A field round's answers as `attachKycInfo`'s argument, or `undefined` when the
 * round carried none of them.
 *
 * Stripe's guidance is to collect these in our own UI and pass them in, so this
 * is the whole of what its field sheet would otherwise have asked for.
 */
export function stripeKycInfoFrom(
  values: FormValues,
): StripeKycInfo | undefined {
  const address = compact<StripeKycAddress>({
    line1: text(values.RESIDENTIAL_ADDRESS_LINE_1),
    line2: text(values.RESIDENTIAL_ADDRESS_LINE_2),
    city: text(values.RESIDENTIAL_ADDRESS_CITY),
    state: text(values.RESIDENTIAL_ADDRESS_REGION),
    postalCode: text(values.RESIDENTIAL_ADDRESS_POSTAL_CODE),
    country: text(values.RESIDENTIAL_ADDRESS_COUNTRY),
  })
  const info = compact<StripeKycInfo>({
    firstName: text(values.LEGAL_FIRST_NAME),
    lastName: text(values.LEGAL_LAST_NAME),
    idNumber: text(values.TAX_IDENTIFIER),
    dateOfBirth: birthDate(values.DATE_OF_BIRTH),
    ...(Object.keys(address).length === 0 ? {} : { address }),
  })
  return Object.keys(info).length === 0 ? undefined : info
}

/**
 * Stripe wants a numeric triple where the wire carries a zoneless `YYYY-MM-DD`.
 *
 * Read off `parseCalendarDate`, which answers local midnight and round-trips the
 * value, so the components come back exactly as typed and an impossible day is
 * refused rather than rolled into the next month.
 */
function birthDate(value: JsonValue | undefined): StripeKycInfo['dateOfBirth'] {
  const raw = text(value)
  const parsed = raw === undefined ? undefined : parseCalendarDate(raw)
  return parsed === undefined
    ? undefined
    : {
        day: parsed.getDate(),
        month: parsed.getMonth() + 1,
        year: parsed.getFullYear(),
      }
}

function compact<T extends object>(value: {
  [K in keyof T]?: T[K] | undefined
}): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  ) as T
}

function text(value: JsonValue | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}
